import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { FaEdit, FaEye } from "react-icons/fa";
import dayjs from "dayjs";
import "../../styles/Form.css";
import "../../styles/AdminDashboard.css";
import Pagination from "../../components/Pagination";
import LeftPanelPersonal from "./LeftPanelPersonal";
import RightPanel from "./RightPanel";
import useAccess from "../../hooks/useAccess";
import StyledSelect from "../../components/StyledSelect";
import DateRangeFilter from "../../components/DateRangeFilter";

function normalizeSortValue(value) {
  if (value == null) return "";
  if (typeof value === "number") return value;

  const text = String(value).trim();
  if (!text) return "";

  const numeric = Number(text.replace(/,/g, ""));
  if (!Number.isNaN(numeric) && /^[-+]?\d[\d,.]*$/.test(text)) {
    return numeric;
  }

  const dateValue = Date.parse(text);
  if (!Number.isNaN(dateValue) && /(\d{4}|\d{1,2}[/-]\d{1,2})/.test(text)) {
    return dateValue;
  }

  return text.toLowerCase();
}

function formatDriverLicenseClass(value) {
  if (Array.isArray(value)) {
    const classes = value
      .map((item) => String(item).trim())
      .filter(Boolean);
    return classes.length ? classes.join(", ") : "-";
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "-";

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return formatDriverLicenseClass(parsed);
      }
    } catch {
      // Fall back to plain string formatting below.
    }

    return trimmed.includes(",")
      ? trimmed
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
          .join(", ") || "-"
      : trimmed;
  }

  return value == null ? "-" : String(value);
}

function AdminDashboard() {
  // 🌐 API & Constants
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // 📦 React Router & Navigation
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get("tab") || "users";

  // 📦 State: UI & Control
  const [tab, setTab] = useState(initialTab);
  const [slidePanelVisible, setSlidePanelVisible] = useState(false);
  const [slideDirection, setSlideDirection] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  // 🔍 State: Filters & Query (legacy name-based from URL)
  const [empCategoryFilter, setEmpCategoryFilter] = useState("All");
  const [terminalFilter, setTerminalFilter] = useState("All");
  const [provinceFilter, setProvinceFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("Associate");
  const [statusOptions, setStatusOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusChangeFrom, setStatusChangeFrom] = useState("");
  const [statusChangeTo, setStatusChangeTo] = useState("");

  // 🗃️ State: Data Collections (restore all so tabs work)
  const [users, setUsers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [driverInfos, setDriverInfos] = useState([]);
  const [healthCardInfos, setHealthCardInfos] = useState([]);
  const [passportInfos, setPassportInfos] = useState([]);
  const [fastCardInfos, setFastCardInfos] = useState([]);
  const [prCardInfos, setPrCardInfos] = useState([]);
  const [usVisaInfos, setUsVisaInfos] = useState([]);
  const [workPermitInfos, setWorkPermitInfos] = useState([]);
  const [emergencyInfos, setEmergencyInfos] = useState([]);
  const [vacationInfos, setVacationInfos] = useState([]);
  const [workHistoryInfos, setWorkHistoryInfos] = useState([]);
  const [directDepositInfos, setDirectDepositInfos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orgUnit, setOrgUnit] = useState([]);
  const [countries, setCountries] = useState([]);
  const [vacationTypes, setVacationTypes] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [driverManagers, setDriverManagers] = useState([]);
  const [fuelCardInfos, setFuelCardInfos] = useState([]);

  // 🧪 State: Access & Auth
  const { hasPermission } = useAccess();

  // 🚫 TAB-LEVEL ACCESS CONTROL — prevents manual URL access
  const TAB_PERMISSIONS = {
    drivers: ["AdminDashboard", "Driver License", "view"],
    healthcards: ["AdminDashboard", "Health Card", "view"],
    fastcards: ["AdminDashboard", "Fast Card", "view"],
    prcards: ["AdminDashboard", "PR Card", "view"],
    usvisa: ["AdminDashboard", "US Visa", "view"],
    passports: ["AdminDashboard", "Passport", "view"],
    workpermits: ["AdminDashboard", "Work Permit", "view"],
    workhistory: ["AdminDashboard", "Work History", "view"],
    emergency: ["AdminDashboard", "Emergency", "view"],
    vacation: ["AdminDashboard", "Vacation", "view"],
    directdeposit: ["AdminDashboard", "Direct Deposit", "view"],
    fuelcards: ["AdminDashboard", "Fuel Card", "view"],
  };

  // ⭐ NEW: Centralized PERMISSION Map
  const PERMISSIONS = {
    users: {
      create: hasPermission("AdminDashboard", "User", "create"),
      edit: hasPermission("AdminDashboard", "User", "edit"),
      view: hasPermission("AdminDashboard", "User", "view"),
      assign: hasPermission("AdminDashboard", "User", "assign"),
      delete: hasPermission("AdminDashboard", "User", "delete"),
    },
    drivers: {
      edit: hasPermission("AdminDashboard", "Driver License", "edit"),
    },
    healthcards: {
      edit: hasPermission("AdminDashboard", "Health Card", "edit"),
    },
    passports: {
      edit: hasPermission("AdminDashboard", "Passport", "edit"),
    },
    fastcards: {
      edit: hasPermission("AdminDashboard", "Fast Card", "edit"),
    },
    prcards: {
      edit: hasPermission("AdminDashboard", "PR Card", "edit"),
    },
    usvisa: {
      edit: hasPermission("AdminDashboard", "US Visa", "edit"),
    },
    workpermits: {
      edit: hasPermission("AdminDashboard", "Work Permit", "edit"),
    },
    workhistory: {
      edit: hasPermission("AdminDashboard", "Work History", "edit"),
    },
    emergency: {
      edit: hasPermission("AdminDashboard", "Emergency", "edit"),
    },
    vacation: {
      edit: hasPermission("AdminDashboard", "Vacation", "edit"),
      delete: hasPermission("AdminDashboard", "Vacation", "delete"),
    },
    directdeposit: {
      edit: hasPermission("AdminDashboard", "Direct Deposit", "edit"),
    },
    fuelcards: {
      edit: hasPermission("AdminDashboard", "Fuel Card", "edit"),
    },
  };

  // 📄 Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [usersSortConfig, setUsersSortConfig] = useState(null);
  const usersTableRef = useRef(null);
  const itemsPerPage = 20;

  // 🔄 Stable helper to fetch users (role = 2 only)
  const loadUsers = useCallback(async () => {
    const token = localStorage.getItem("token");
    const { data } = await axios.get(`${BASE_URL}/api/users`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const onlyRole2 = Array.isArray(data)
      ? data.filter((u) => Number(u.role) === 2 && Number(u.re_applied) === 0)
      : [];
    setUsers(onlyRole2);
  }, [BASE_URL]);

  const getPositionNumber = useCallback(
    (positionFull) => {
      const match = positions.find(
        (p) => String(p.p_id) === String(positionFull),
      );
      return match?.p_position_number || positionFull || "-";
    },
    [positions],
  );

  // Initial fetches
  useEffect(() => {
    if (location.state?.tab) setTab(location.state.tab);

    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };

    const loadCustomTaskAdminList = (taskKey, setter) => {
      axios
        .get(`${BASE_URL}/api/custom-tasks/admin/list/${taskKey}`, { headers })
        .then((res) => setter(res.data))
        .catch((err) =>
          console.error(`Failed to load ${taskKey} admin list`, err),
        );
    };

    // Users
    loadUsers();

    axios
      .get(`${BASE_URL}/api/positions`, { headers })
      .then((res) => setPositions(res.data || []))
      .catch((err) => console.error("Failed to load positions", err));

    // Admin tools datasets (restore so tabs work)
    axios
      .get(`${BASE_URL}/api/driverlicense/admin/all`)
      .then((res) => setDriverInfos(res.data));
    loadCustomTaskAdminList("health_cards", setHealthCardInfos);
    loadCustomTaskAdminList("fast_card", setFastCardInfos);
    loadCustomTaskAdminList("fuel_card", setFuelCardInfos);
    loadCustomTaskAdminList("passport", setPassportInfos);
    loadCustomTaskAdminList("pr_card", setPrCardInfos);
    loadCustomTaskAdminList("us_visa", setUsVisaInfos);
    loadCustomTaskAdminList("work_permit", setWorkPermitInfos);
    axios
      .get(`${BASE_URL}/api/emergencycontact/admin/all`)
      .then((res) => setEmergencyInfos(res.data));
    axios
      .get(`${BASE_URL}/api/vacationform/admin/all`)
      .then((res) => setVacationInfos(res.data));
    axios
      .get(`${BASE_URL}/api/work-history/admin/all`)
      .then((res) => setWorkHistoryInfos(res.data));
    axios
      .get(`${BASE_URL}/api/directdeposit/admin/all`)
      .then((res) => setDirectDepositInfos(res.data));
  }, [BASE_URL, navigate, location.state, loadUsers]);

  // Read legacy filters from URL (keep behavior like before)
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const updatedTab = qs.get("tab") || "users";
    const updatedEmpFilter = qs.get("filter") || "All";
    const updatedStatus = qs.get("status") || "Associate";
    setTab(updatedTab);
    setEmpCategoryFilter(updatedEmpFilter);
    setStatusFilter(updatedStatus);
  }, [location.search]);

  // Dropdown data
  useEffect(() => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };
    axios
      .get(`${BASE_URL}/api/dropdowns/categories`, { headers })
      .then((res) => setCategories(res.data));
    axios
      .get(`${BASE_URL}/api/dropdowns/org-units`, { headers })
      .then((res) => setOrgUnit(res.data));
    axios
      .get(`${BASE_URL}/api/dropdowns/user-status`, { headers })
      .then((res) => setStatusOptions(res.data));
    axios
      .get(`${BASE_URL}/api/dropdowns/countries`, { headers })
      .then((res) => setCountries(res.data));
    axios
      .get(`${BASE_URL}/api/dropdowns/vacation-type`, { headers })
      .then((res) => setVacationTypes(res.data));
    axios
      .get(`${BASE_URL}/api/dropdowns/recruiters`, { headers })
      .then((res) => setRecruiters(res.data));
    axios
      .get(`${BASE_URL}/api/dropdowns/driver-managers`, { headers })
      .then((res) => setDriverManagers(res.data));
  }, [BASE_URL]);

  // Reset page on search/filter
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchQuery,
    empCategoryFilter,
    terminalFilter,
    statusFilter,
    statusChangeFrom,
    statusChangeTo,
  ]);

  const handleDelete = (id, type) => {
    let url = "";
    if (type === "user") url = `/api/users/${id}`;
    else if (type === "vacation") url = `/api/vacationform/${id}`;

    if (!url) return;

    if (window.confirm(`Are you sure you want to delete this ${type}?`)) {
      axios
        .delete(`${BASE_URL}${url}`)
        .then(() => {
          alert(`${type} Deleted successfully`);
          if (type === "user") {
            loadUsers();
          } else if (type === "vacation") {
            axios
              .get(`${BASE_URL}/api/vacationform/admin/all`)
              .then((res) => setVacationInfos(res.data));
          }
        })
        .catch(() => alert(`Failed to delete ${type}`));
    }
  };

  const handleEdit = (id, type, wpId) => {
    if (type === "user") navigate(`/userhome/${id}`);
    else if (type === "health_cards") navigate(`/task/health_cards/${id}`);
    else if (type === "pr_card") navigate(`/task/pr_card/${id}`);
    else if (type === "us_visa") navigate(`/task/us_visa/${id}`);
    else if (type === "fuel_card") navigate(`/task/fuel_card/${id}`);
    else if (type === "fast_card") navigate(`/task/fast_card/${id}`);
    else if (type === "passport") navigate(`/task/passport/${id}`);
    else if (type === "workpermit") navigate(`/task/work_permit/${id}`);
    else navigate(`/edit-${type}/${id}`);
  };

  // Helpers to map legacy name filters to IDs for comparison
  const getCategoryIdByName = (name) => {
    if (!name || name === "All") return null;
    const cat = categories.find((c) => c.c_name === name);
    return cat ? String(cat.c_id) : null;
  };
  const getCategoryIdsByFilter = (filterValue) => {
    if (!filterValue || filterValue === "All") return [];

    return filterValue
      .split(",")
      .map((name) => getCategoryIdByName(name.trim()))
      .filter(Boolean);
  };
  const getStatusIdByName = (name) => {
    if (!name || name === "All") return null;
    const s = statusOptions.find((s) => s.s_TranslatedText === name);
    return s ? String(s.s_id) : null;
  };

  // Display helpers
  const getCategoryName = (categoryId) => {
    const cat = categories.find((c) => c.c_id === parseInt(categoryId));
    return cat ? cat.c_translatedText : categoryId;
  };
  const getOrgUnit = (orgUnitId) => {
    const ou = orgUnit.find((ou) => ou.ou_id === parseInt(orgUnitId));
    return ou ? ou.ou_name : orgUnitId;
  };
  const getCountryName = (countryId) => {
    const cn = countries.find((cn) => cn.id === parseInt(countryId));
    return cn ? cn.country_name : countryId;
  };
  const getVacationTypeName = (typeId) => {
    const vt = vacationTypes.find((vt) => vt.vt_id === parseInt(typeId));
    return vt ? vt.vt_name : typeId;
  };
  const getRecruiterName = (userId) => {
    const m = recruiters.find((m) => m.value === parseInt(userId));
    return m ? m.label : userId;
  };
  const getDriverManagerName = (userId) => {
    const dm = driverManagers.find((dm) => dm.value === parseInt(userId));
    return dm ? dm.label : userId;
  };

  // Unified filter — works for legacy path filters (names)
  const applySearchFilter = (
    data,
    categoryField = "emp_category",
    statusField = "status",
  ) => {
    const categoryIds = getCategoryIdsByFilter(empCategoryFilter);
    const categoryNames = empCategoryFilter
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean);
    const statusId = getStatusIdByName(statusFilter);

    return data.filter((item) => {
      const matchesSearch = Object.values(item).some((val) =>
        val?.toString().toLowerCase().includes(searchQuery.toLowerCase()),
      );

      let categoryMatch = true;
      if (tab === "workhistory") {
        categoryMatch =
          empCategoryFilter === "All" ||
          categoryNames.includes(item[categoryField]);
      } else {
        categoryMatch =
          empCategoryFilter === "All" ||
          categoryIds.includes(item[categoryField]?.toString());
      }

      const statusMatch =
        tab !== "users" ||
        statusFilter === "All" ||
        item[statusField]?.toString() === statusId;

      const terminalMatch =
        tab !== "users" ||
        terminalFilter === "All" ||
        item.org_unit?.toString() === terminalFilter;

      const statusChangeDate = item.status_change_date || "";
      const statusChangeFromMatch =
        tab !== "users" ||
        !statusChangeFrom ||
        (statusChangeDate && statusChangeDate >= statusChangeFrom);
      const statusChangeToMatch =
        tab !== "users" ||
        !statusChangeTo ||
        (statusChangeDate && statusChangeDate <= statusChangeTo);

      const provinceMatch =
        tab !== "drivers" ||
        provinceFilter === "All" ||
        item.dl_province === provinceFilter;

      return (
        matchesSearch &&
        categoryMatch &&
        statusMatch &&
        terminalMatch &&
        statusChangeFromMatch &&
        statusChangeToMatch &&
        provinceMatch
      );
    });
  };

  // Fetch full user when opening panels
  const fetchUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${BASE_URL}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data;
    } catch (err) {
      console.error("❌ Failed to fetch full user info:", err);
      return null;
    }
  };

  const getPaginatedData = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  useEffect(() => {
    const table = usersTableRef.current;
    if (!table) return undefined;

    const handleClientSort = (event) => {
      setCurrentPage(1);
      setUsersSortConfig({
        columnIndex: event.detail.columnIndex,
        direction: event.detail.direction,
      });
    };

    table.addEventListener("nw-client-sort", handleClientSort);
    return () => table.removeEventListener("nw-client-sort", handleClientSort);
  });

  const filteredUsers = applySearchFilter(users);

  const sortedUsers = (() => {
    if (!usersSortConfig) return filteredUsers;

    const accessors = [
      (user) => user.userid_no,
      (user) => user.full_name,
      (user) =>
        `${user.position} - ${getPositionNumber(user.position_full)} - ${
          getOrgUnit(user.org_unit) || "-"
        }`,
      (user) => getCategoryName(user.emp_category),
      (user) =>
        user.driver_manager && user.driver_manager.toString().trim() !== ""
          ? getDriverManagerName(user.driver_manager)
          : getRecruiterName(user.emp_recruiter) || "-",
      (user) => getCountryName(user.country),
      (user) => user.days_since_status_change,
    ];
    const getValue = accessors[usersSortConfig.columnIndex];
    if (!getValue) return filteredUsers;

    return [...filteredUsers].sort((left, right) => {
      const leftRawValue = getValue(left);
      const rightRawValue = getValue(right);
      const leftIsMissing =
        leftRawValue === null ||
        leftRawValue === undefined ||
        String(leftRawValue).trim() === "";
      const rightIsMissing =
        rightRawValue === null ||
        rightRawValue === undefined ||
        String(rightRawValue).trim() === "";

      if (leftIsMissing !== rightIsMissing) {
        return leftIsMissing ? 1 : -1;
      }

      const leftValue = normalizeSortValue(leftRawValue);
      const rightValue = normalizeSortValue(rightRawValue);

      if (leftValue < rightValue) {
        return usersSortConfig.direction === "asc" ? -1 : 1;
      }
      if (leftValue > rightValue) {
        return usersSortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  })();

  const empCategoryOptions =
    tab === "workhistory"
      ? [
          { value: "All", label: "All" },
          { value: "Working", label: "Working" },
          { value: "School", label: "School" },
          { value: "Driving School", label: "Driving School" },
          { value: "Military", label: "Military" },
          { value: "Unemployed", label: "Unemployed" },
        ]
      : [
          { value: "All", label: "All" },
          {
            value: "Driver,Owner Operator,Independent Contractor,Agency Driver,Agent",
            label: "Drivers",
          },
          {
            value: "Office Staff,Shop,Non Driving Owner Operator",
            label: "Non-Drivers",
          },
          ...categories.map((cat) => ({
            value: cat.c_name,
            label: cat.c_name,
          })),
        ];

  const statusSelectOptions = [
    { value: "All", label: "All" },
    ...statusOptions.map((status) => ({
      value: status.s_TranslatedText,
      label: status.s_TranslatedText,
    })),
  ];

  const terminalSelectOptions = [
    { value: "All", label: "All Terminals" },
    ...orgUnit.map((terminal) => ({
      value: String(terminal.ou_id),
      label: terminal.ou_name,
    })),
  ];

  const provinceSelectOptions = [
    { value: "All", label: "All Provinces" },
    ...[
      ...new Set(driverInfos.map((driver) => driver.dl_province).filter(Boolean)),
    ].map((province) => ({
      value: province,
      label: province,
    })),
  ];

  // Check permission for the current tab
  let canViewTab = true;
  if (TAB_PERMISSIONS[tab]) {
    const [page, resource, action] = TAB_PERMISSIONS[tab];
    canViewTab = hasPermission(page, resource, action);
  }

  if (!canViewTab) {
    return (
      <div className="container py-5 text-center">
        <h4 className="text-danger">Access Denied</h4>
        <p>You do not have permission to view this section.</p>
      </div>
    );
  }

  return (
    <div className="form-wrapper my-3">
      <div className="dashboard-header d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
        <h3 className="fw-bold m-0">
          {tab === "users" && "🧑‍💼 All Associates"}
          {tab === "drivers" && "🚗 Driver Licenses"}
          {tab === "healthcards" && "💳 Health Cards"}
          {tab === "passports" && "🛂 Passports"}
          {tab === "fastcards" && "⚡️ Fast Cards"}
          {tab === "prcards" && "PR Cards"}
          {tab === "usvisa" && "US Visa"}
          {tab === "workpermits" && "💼 Work Permits"}
          {tab === "emergency" && "🚨 Emergency Contacts"}
          {tab === "vacation" && "🌴 Vacation Requests"}
          {tab === "incident" && "⚠️ Incident Reports"}
          {tab === "workhistory" && "📄 Work History"}
          {tab === "directdeposit" && "🏦 Direct Deposit Files"}
          {tab === "fuelcards" && "⛽ Fuel Cards"}
        </h3>

        {tab === "users" && PERMISSIONS.users.create && (
          <button
            className="btn btn-success dashboard-add-button"
            onClick={() => navigate("/form")}
          >
            Add New User
          </button>
        )}
      </div>

      <div className="row align-items-center mb-3 nw-filter-bar">
        <div className="col-auto">
          <input
            type="text"
            className="form-control"
            placeholder="Search by any field..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ minWidth: "250px" }}
          />
        </div>

        {["users", "workhistory"].includes(tab) && (
          <div className="col-auto">
            <StyledSelect
              value={empCategoryFilter}
              options={empCategoryOptions}
              onChange={setEmpCategoryFilter}
              minWidth={310}
              ariaLabel="Filter by category"
            />
          </div>
        )}

        {tab === "users" && (
          <div className="col-auto">
            <StyledSelect
              value={terminalFilter}
              options={terminalSelectOptions}
              onChange={setTerminalFilter}
              minWidth={220}
              ariaLabel="Filter by terminal"
            />
          </div>
        )}

        {tab === "users" && (
          <div className="col-auto">
            <StyledSelect
              value={statusFilter}
              options={statusSelectOptions}
              onChange={setStatusFilter}
              minWidth={180}
              ariaLabel="Filter by status"
            />
          </div>
        )}

        {tab === "users" && (
          <div className="col-auto">
            <DateRangeFilter
              fromValue={statusChangeFrom}
              toValue={statusChangeTo}
              onFromChange={setStatusChangeFrom}
              onToChange={setStatusChangeTo}
              label="Status change date"
            />
          </div>
        )}

        {tab === "drivers" && (
          <div className="col-auto">
            <StyledSelect
              value={provinceFilter}
              options={provinceSelectOptions}
              onChange={setProvinceFilter}
              minWidth={200}
              ariaLabel="Filter by province"
            />
          </div>
        )}
      </div>

      {/* USERS */}
      {tab === "users" && (
        <div className="table-responsive">
          <table
            ref={usersTableRef}
            className="table table-hover table-bordered table-striped align-middle"
            data-nw-client-sort="true"
          >
            <thead className="table-dark text-center">
              <tr>
                <th>Employee ID</th>
                <th>Full Name</th>
                <th>Position</th>
                <th>Category</th>
                <th>Assignee</th>
                <th>Country</th>
                <th className="text-center">Days Since Updated</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedData(sortedUsers).map((u) => (
                <tr key={u.id}>
                  <td>{u.userid_no}</td>
                  <td
                    onMouseEnter={() => {
                      const timer = setTimeout(async () => {
                        const fullUser = await fetchUserDetails(u.id);
                        if (fullUser) {
                          setSelectedUser(fullUser);
                          setSlideDirection("right");
                          setSlidePanelVisible(true);
                        }
                      }, 1500);
                      u._hoverTimer = timer;
                    }}
                    onMouseLeave={() => {
                      if (u._hoverTimer) clearTimeout(u._hoverTimer);
                      u._hoverTimer = null;
                    }}
                    onClick={async () => {
                      const fullUser = await fetchUserDetails(u.id);
                      if (fullUser) {
                        setSelectedUser(fullUser);
                        setSlideDirection("left");
                        setSlidePanelVisible(true);
                      }
                    }}
                    style={{
                      cursor: "pointer",
                      color: "blue",
                      textDecoration: "underline",
                    }}
                  >
                    {u.full_name}
                  </td>

                  <td>
                    {u.position} - {getPositionNumber(u.position_full)} -{" "}
                    {getOrgUnit(u.org_unit) || "-"}
                  </td>
                  <td>{getCategoryName(u.emp_category)}</td>
                  <td>
                    {u.driver_manager &&
                    u.driver_manager.toString().trim() !== ""
                      ? getDriverManagerName(u.driver_manager)
                      : getRecruiterName(u.emp_recruiter) || "-"}
                  </td>
                  <td>{getCountryName(u.country)}</td>
                  <td
                    className="text-center"
                    title={
                      u.status_change_date
                        ? `Status last changed on ${u.status_change_date}`
                        : "No status change history"
                    }
                  >
                    {u.days_since_status_change ?? "-"}
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => {
                        const isAdmin = Number(u.role) === 1;
                        const isAssociate = Number(u.status) === 1; // status 1
                        const isCandidate = Number(u.status) === 3; // status 3

                        if (isAssociate) {
                          // 1) Associates → go to that user's userHome
                          navigate(`/userhome/${u.id}`);
                          return;
                        }

                        if (isAdmin) {
                          // 3) Admins → edit page; Return should go back to /admin-users
                          navigate(`/edit-user/${u.id}`, {
                            state: { returnTo: "/admin-users" },
                          });
                          return;
                        }

                        if (isCandidate) {
                          // 2) Candidates → go to userHome
                          navigate(`/userhome/${u.id}`);
                          return;
                        }

                        // sensible default
                        navigate(`/userhome/${u.id}`);
                      }}
                      disabled={!PERMISSIONS.users.view}
                    >
                      <FaEye />
                    </button>

                    {PERMISSIONS.users.delete && (
                      <button
                        className="btn btn-danger btn-sm btn-action"
                        onClick={() => handleDelete(u.id, "user")}
                      >
                        🗑️
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={filteredUsers.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* DRIVER LICENSES */}
      {tab === "drivers" && (
        <div className="table-responsive">
          <table className="table table-hover table-bordered table-striped align-middle">
            <thead className="table-dark text-center">
              <tr>
                <th>User ID</th>
                <th>User Name</th>
                <th>Class Name</th>
                <th>Expiry Date</th>
                <th>Province</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedData(applySearchFilter(driverInfos)).map((d) => (
                <tr key={d.dl_id}>
                  <td>{d.userid_no || "-"}</td>
                  <td>{d.full_name || "-"}</td>
                  <td>{formatDriverLicenseClass(d.dl_class)}</td>
                  <td>
                    {d.dl_expdate ? dayjs(d.dl_expdate).format("YYYY-MM-DD") : "-"}
                  </td>
                  <td>{d.dl_province || "-"}</td>
                  <td className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(d.dl_userid, "driver")}
                      disabled={!PERMISSIONS.drivers.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(driverInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* HEALTH CARDS */}
      {tab === "healthcards" && (
        <div className="table-responsive">
          <table className="table table-hover table-bordered table-striped align-middle">
            <thead className="table-dark text-center">
              <tr>
                <th>User ID</th>
                <th>User Name</th>
                <th>Expiry Date</th>
                <th>Files</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedData(applySearchFilter(healthCardInfos)).map((h) => (
                <tr key={h.user_id}>
                  <td>{h.userid_no || "-"}</td>
                  <td>{h.full_name || "-"}</td>
                  <td>
                    {h.expiry_date
                      ? dayjs(h.expiry_date).format("YYYY-MM-DD")
                      : "-"}
                  </td>
                  <td>{h.attachment_count || 0}</td>
                  <td className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(h.user_id, "health_cards")}
                      disabled={!PERMISSIONS.healthcards.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(healthCardInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Fast Cards */}
      {tab === "fastcards" && (
        <div className="table-responsive">
          <table className="table table-hover table-bordered table-striped align-middle">
            <thead className="table-dark text-center">
              <tr>
                <th>User ID</th>
                <th>User Name</th>
                <th>Card Number</th>
                <th>Expiry Date</th>
                <th>Files</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedData(applySearchFilter(fastCardInfos)).map((f) => (
                <tr key={f.user_id}>
                  <td>{f.userid_no || "-"}</td>
                  <td>{f.full_name || "-"}</td>
                  <td>{f.card_number || "-"}</td>
                  <td>
                    {f.expiry_date
                      ? dayjs(f.expiry_date).format("YYYY-MM-DD")
                      : "-"}
                  </td>
                  <td>{f.attachment_count || 0}</td>
                  <td className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(f.user_id, "fast_card")}
                      disabled={!PERMISSIONS.fastcards.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(fastCardInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Passports */}
      {tab === "passports" && (
        <div className="table-responsive">
          <table className="table table-hover table-bordered table-striped align-middle">
            <thead className="table-dark text-center">
              <tr>
                <th>User ID</th>
                <th>User Name</th>
                <th>Expiry Date</th>
                <th>Issue Country</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedData(applySearchFilter(passportInfos)).map((p) => (
                <tr key={p.user_id}>
                  <td>{p.userid_no || "-"}</td>
                  <td>{p.full_name || "-"}</td>
                  <td>
                    {p.expiry_date
                      ? dayjs(p.expiry_date).format("YYYY-MM-DD")
                      : "-"}
                  </td>
                  <td>{p.issue_country || "-"}</td>
                  <td className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(p.user_id, "passport")}
                      disabled={!PERMISSIONS.passports.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(passportInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* PR Cards */}
      {tab === "prcards" && (
        <div className="table-responsive">
          <table className="table table-hover table-bordered table-striped align-middle">
            <thead className="table-dark text-center">
              <tr>
                <th>User ID</th>
                <th>User Name</th>
                <th>Expiry Date</th>
                <th>Files</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedData(applySearchFilter(prCardInfos)).map((p) => (
                <tr key={p.user_id}>
                  <td>{p.userid_no || "-"}</td>
                  <td>{p.full_name || "-"}</td>
                  <td>
                    {p.expiry_date
                      ? dayjs(p.expiry_date).format("YYYY-MM-DD")
                      : "-"}
                  </td>
                  <td>{p.attachment_count || 0}</td>
                  <td className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(p.user_id, "pr_card")}
                      disabled={!PERMISSIONS.prcards.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(prCardInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* US Visa */}
      {tab === "usvisa" && (
        <div className="table-responsive">
          <table className="table table-hover table-bordered table-striped align-middle">
            <thead className="table-dark text-center">
              <tr>
                <th>User ID</th>
                <th>User Name</th>
                <th>Expiry Date</th>
                <th>Files</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedData(applySearchFilter(usVisaInfos)).map((v) => (
                <tr key={v.user_id}>
                  <td>{v.userid_no || "-"}</td>
                  <td>{v.full_name || "-"}</td>
                  <td>
                    {v.expiry_date
                      ? dayjs(v.expiry_date).format("YYYY-MM-DD")
                      : "-"}
                  </td>
                  <td>{v.attachment_count || 0}</td>
                  <td className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(v.user_id, "us_visa")}
                      disabled={!PERMISSIONS.usvisa.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(usVisaInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Work Permits */}
      {tab === "workpermits" && (
        <div className="table-responsive">
          <table className="table table-hover table-bordered table-striped align-middle">
            <thead className="table-dark text-center">
              <tr>
                <th>User ID</th>
                <th>User Name</th>
                <th>Expiry Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedData(applySearchFilter(workPermitInfos)).map((w) => (
                <tr key={w.user_id}>
                  <td>{w.userid_no || "-"}</td>
                  <td>{w.full_name || "-"}</td>
                  <td>
                    {w.expiry_date
                      ? dayjs(w.expiry_date).format("YYYY-MM-DD")
                      : "-"}
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(w.user_id, "workpermit")}
                      disabled={!PERMISSIONS.workpermits.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(workPermitInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* WORK HISTORY (list view — the slide panel shows only Working) */}
      {tab === "workhistory" && (
        <div className="table-responsive">
          <table className="table table-hover table-bordered table-striped align-middle">
            <thead className="table-dark text-center">
              <tr>
                <th>User ID</th>
                <th>User Name</th>
                <th>Type</th>
                <th>Company</th>
                <th>Country</th>
                <th>From</th>
                <th>To</th>
                <th>Position</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedData(
                applySearchFilter(workHistoryInfos, "wh_type"),
              ).map((w) => (
                <tr key={w.wh_id}>
                  <td>{w.userid_no || "-"}</td>
                  <td>{w.full_name || "-"}</td>
                  <td>{w.wh_type || "-"}</td>
                  <td>{w.wh_name || "-"}</td>
                  <td>{w.wh_country || "-"}</td>
                  <td>{dayjs(w.wh_datefrom).format("YYYY-MM-DD") || "-"}</td>
                  <td>
                    {w.wh_dateto
                      ? dayjs(w.wh_dateto).format("YYYY-MM-DD")
                      : "Present" || "-"}
                  </td>
                  <td>{w.wh_position || "-"}</td>
                  <td className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(w.wh_userid, "workhistory")}
                      disabled={!PERMISSIONS.workhistory.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(workHistoryInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* EMERGENCY CONTACTS */}
      {tab === "emergency" && (
        <div className="table-responsive">
          <table className="table table-hover table-bordered table-striped align-middle">
            <thead className="table-dark text-center">
              <tr>
                <th>User ID</th>
                <th>User Name</th>
                <th>Contact Name</th>
                <th>Contact Phone</th>
                <th>Contact Email</th>
                <th>Relation</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedData(applySearchFilter(emergencyInfos)).map((ec) => (
                <tr key={ec.ec_id}>
                  <td>{ec.userid_no || "-"}</td>
                  <td>{ec.full_name || "-"}</td>
                  <td>{ec.ec1_name || "-"}</td>
                  <td>{ec.ec1_phone || "-"}</td>
                  <td>{ec.ec1_email || "-"}</td>
                  <td>{ec.ec1_relationship || "-"}</td>
                  <td className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(ec.ec_userid, "emergency")}
                      disabled={!PERMISSIONS.emergency.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(emergencyInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* VACATION FORMS */}
      {tab === "vacation" && (
        <div className="table-responsive">
          <table className="table table-hover table-bordered table-striped align-middle">
            <thead className="table-dark text-center">
              <tr>
                <th>User ID</th>
                <th>User Name</th>
                <th>Type</th>
                <th>Manager</th>
                <th>From Date</th>
                <th>To Date</th>
                <th>File Name</th>
                <th>Submitted Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedData(applySearchFilter(vacationInfos)).map((v) => (
                <tr key={v.vf_id}>
                  <td>{v.userid_no || "-"}</td>
                  <td>{v.full_name || "-"}</td>
                  <td>{getVacationTypeName(v.vf_type) || "-"}</td>
                  <td>{getRecruiterName(v.vf_manager) || "-"}</td>
                  <td>{dayjs(v.vf_from_date).format("YYYY-MM-DD") || "-"}</td>
                  <td>{dayjs(v.vf_to_date).format("YYYY-MM-DD") || "-"}</td>
                  <td>{v.vf_form_original || "-"}</td>
                  <td>
                    {dayjs(v.vf_submitted_date).format("YYYY-MM-DD") || "-"}
                  </td>
                  <td className="text-center">
                    {/* ✏️ Edit permission */}
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(v.vf_userid, "vacation")}
                      disabled={!PERMISSIONS.users.edit}
                    >
                      <FaEdit />
                    </button>

                    {/* 🗑️ Delete permission */}
                    {PERMISSIONS.vacation.delete && (
                      <button
                        className="btn btn-danger btn-sm btn-action"
                        onClick={() => handleDelete(v.vf_id, "vacation")}
                      >
                        🗑️
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(vacationInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* DIRECT DEPOSIT */}
      {tab === "directdeposit" && (
        <div className="table-responsive">
          <table className="table table-hover table-bordered table-striped align-middle">
            <thead className="table-dark text-center">
              <tr>
                <th>User ID</th>
                <th>User Name</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedData(applySearchFilter(directDepositInfos)).map(
                (dd) => (
                  <tr key={dd.dd_id}>
                    <td>{dd.userid_no || "-"}</td>
                    <td>{dd.full_name || "-"}</td>
                    <td className="text-center">
                      <button
                        className="btn btn-warning btn-sm me-2"
                        onClick={() =>
                          handleEdit(dd.dd_userid, "directdeposit")
                        }
                        disabled={!PERMISSIONS.directdeposit.edit}
                      >
                        <FaEdit />
                      </button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(directDepositInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* FUEL CARDS */}
      {tab === "fuelcards" && (
        <div className="table-responsive">
          <table className="table table-hover table-bordered table-striped align-middle">
            <thead className="table-dark text-center">
              <tr>
                <th>User ID</th>
                <th>User Name</th>
                <th>Card Number</th>
                <th>Expiry Date</th>
                <th>Files</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedData(applySearchFilter(fuelCardInfos)).map((fc) => (
                <tr key={fc.user_id}>
                  <td>{fc.userid_no || "-"}</td>
                  <td>{fc.full_name || "-"}</td>
                  <td>{fc.card_number || "-"}</td>
                  <td>
                    {fc.expiry_date
                      ? dayjs(fc.expiry_date).format("YYYY-MM-DD")
                      : "-"}
                  </td>
                  <td>{fc.attachment_count || 0}</td>
                  <td className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(fc.user_id, "fuel_card")}
                      disabled={!PERMISSIONS.fuelcards.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(fuelCardInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Slide-over panels */}
      {slidePanelVisible &&
        (slideDirection === "left" ? (
          <LeftPanelPersonal
            user={selectedUser}
            onClose={() => setSlidePanelVisible(false)}
            onSaved={async (updatedId) => {
              // 1) refresh the table
              await loadUsers();

              // 2) refresh the user shown inside the slide so the header/details update too
              const fresh = await fetchUserDetails(updatedId);
              if (fresh) setSelectedUser(fresh);
            }}
          />
        ) : (
          <RightPanel
            user={selectedUser}
            onClose={() => setSlidePanelVisible(false)}
          />
        ))}
    </div>
  );
}

export default AdminDashboard;
